import { useState, useEffect } from 'react';
import { Card, Button, Badge, Input } from '@atoms/index';
import { apiClient } from '@services/api';
import Toast from '@/components/atoms/Toast';
import { cn } from '@/utils/cn';
import { useImageUpload } from '@/hooks/useImageUpload';

interface Banner {
  id: string;
  title: string;
  subtitle: string | null;
  imageUrl: string | null;
  backgroundColor: string | null;
  ctaUrl: string | null;
  platform: 'mobile' | 'web' | 'all';
  displayOrder: number;
  isActive: boolean;
  startDate: string | null;
  endDate: string | null;
  createdAt: string;
}

const PLATFORM_LABELS: Record<string, string> = {
  mobile: 'Mobil',
  web: 'Web',
  all: 'Tümü',
};

const emptyForm = {
  title: '',
  subtitle: '',
  imageUrl: '',
  backgroundColor: '#E63946',
  ctaUrl: '',
  platform: 'all' as Banner['platform'],
  displayOrder: 0,
  isActive: true,
  startDate: '',
  endDate: '',
};

export const BannersTab = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    isVisible: boolean;
    message: string;
    type: 'success' | 'error';
  }>({ isVisible: false, message: '', type: 'success' });

  const imageUpload = useImageUpload({ folder: 'banners' });

  useEffect(() => {
    loadBanners();
  }, []);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ isVisible: true, message, type });
  };

  const loadBanners = async () => {
    setIsLoading(true);
    try {
      const res = await apiClient.get<{ success: boolean; data: Banner[] }>('/admin/banners');
      setBanners(res.data.data ?? []);
    } catch {
      showToast('Bannerlar yüklenemedi', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreate = () => {
    setForm({ ...emptyForm });
    setEditingId(null);
    setIsCreating(true);
  };

  const openEdit = (banner: Banner) => {
    setForm({
      title: banner.title,
      subtitle: banner.subtitle ?? '',
      imageUrl: banner.imageUrl ?? '',
      backgroundColor: banner.backgroundColor ?? '#E63946',
      ctaUrl: banner.ctaUrl ?? '',
      platform: banner.platform,
      displayOrder: banner.displayOrder,
      isActive: banner.isActive,
      startDate: banner.startDate ? banner.startDate.slice(0, 10) : '',
      endDate: banner.endDate ? banner.endDate.slice(0, 10) : '',
    });
    setEditingId(banner.id);
    setIsCreating(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setIsCreating(false);
  };

  const save = async () => {
    if (!form.title.trim()) {
      showToast('Başlık zorunludur', 'error');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        subtitle: form.subtitle.trim() || undefined,
        imageUrl: form.imageUrl.trim() || undefined,
        backgroundColor: form.backgroundColor.trim() || undefined,
        ctaUrl: form.ctaUrl.trim() || undefined,
        platform: form.platform,
        displayOrder: Number(form.displayOrder),
        isActive: form.isActive,
        startDate: form.startDate || undefined,
        endDate: form.endDate || undefined,
      };

      if (editingId) {
        await apiClient.patch(`/admin/banners/${editingId}`, payload);
        showToast('Banner güncellendi', 'success');
      } else {
        await apiClient.post('/admin/banners', payload);
        showToast('Banner oluşturuldu', 'success');
      }
      await loadBanners();
      cancelEdit();
    } catch {
      showToast('İşlem başarısız', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteBanner = async (id: string) => {
    if (!window.confirm('Bu banneri silmek istediğinize emin misiniz?')) return;
    setDeletingId(id);
    try {
      await apiClient.delete(`/admin/banners/${id}`);
      showToast('Banner silindi', 'success');
      await loadBanners();
    } catch {
      showToast('Silinemedi', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const formPanel = (
    <Card padding="lg" className="space-y-4">
      <h3 className="text-lg font-bold text-gray-900">
        {editingId ? 'Banner Düzenle' : 'Yeni Banner'}
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Başlık *</label>
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Banner başlığı"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Alt başlık</label>
          <Input
            value={form.subtitle}
            onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
            placeholder="Kısa açıklama"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Görsel</label>
          {/* Hidden file input */}
          <input
            ref={imageUpload.fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => imageUpload.handleFileChange(e, (url) => setForm({ ...form, imageUrl: url }))}
          />
          <div className="flex gap-2">
            <Input
              value={form.imageUrl}
              onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
              placeholder="https://... veya aşağıdan dosya yükleyin"
              className="flex-1"
            />
            <button
              type="button"
              onClick={imageUpload.triggerFilePicker}
              disabled={imageUpload.isUploading}
              className="flex-shrink-0 px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {imageUpload.isUploading ? '⏳ Yükleniyor…' : '📁 Dosya Seç'}
            </button>
          </div>
          {imageUpload.uploadError && (
            <p className="text-xs text-red-500 mt-1">{imageUpload.uploadError}</p>
          )}
          {form.imageUrl && (
            <img
              src={form.imageUrl}
              alt="önizleme"
              className="mt-2 h-14 rounded-lg object-cover border border-gray-200"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Arka plan rengi</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.backgroundColor}
              onChange={(e) => setForm({ ...form, backgroundColor: e.target.value })}
              className="h-10 w-16 rounded-lg cursor-pointer border border-gray-200"
            />
            <Input
              value={form.backgroundColor}
              onChange={(e) => setForm({ ...form, backgroundColor: e.target.value })}
              placeholder="#E63946"
              className="flex-1"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hedef URL (CTA)</label>
          <Input
            value={form.ctaUrl}
            onChange={(e) => setForm({ ...form, ctaUrl: e.target.value })}
            placeholder="/customer/... veya https://..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
          <select
            value={form.platform}
            onChange={(e) => setForm({ ...form, platform: e.target.value as Banner['platform'] })}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
          >
            <option value="all">Tümü (Mobil + Web)</option>
            <option value="mobile">Sadece Mobil</option>
            <option value="web">Sadece Web</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sıra</label>
          <Input
            type="number"
            min="0"
            value={String(form.displayOrder)}
            onChange={(e) => setForm({ ...form, displayOrder: Number(e.target.value) })}
          />
        </div>
        <div className="flex items-center gap-3 pt-6">
          <input
            id="isActive"
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            className="w-4 h-4 text-primary rounded"
          />
          <label htmlFor="isActive" className="text-sm font-medium text-gray-700">Aktif</label>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç tarihi</label>
          <Input
            type="date"
            value={form.startDate}
            onChange={(e) => setForm({ ...form, startDate: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş tarihi</label>
          <Input
            type="date"
            value={form.endDate}
            onChange={(e) => setForm({ ...form, endDate: e.target.value })}
          />
        </div>
      </div>

      {/* Live preview */}
      {(form.title || form.imageUrl) && (
        <div
          className="rounded-2xl p-5 text-white relative overflow-hidden min-h-[100px] flex items-center"
          style={{ backgroundColor: form.backgroundColor || '#E63946' }}
        >
          {form.imageUrl && (
            <img
              src={form.imageUrl}
              alt=""
              className="absolute right-4 top-1/2 -translate-y-1/2 h-16 w-16 object-contain opacity-90"
            />
          )}
          <div className="flex-1 pr-20">
            <p className="font-bold text-lg leading-tight">{form.title || 'Başlık'}</p>
            {form.subtitle && <p className="text-sm opacity-85 mt-1">{form.subtitle}</p>}
          </div>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <Button variant="primary" onClick={save} isLoading={isSaving} className="rounded-xl">
          {editingId ? 'Güncelle' : 'Oluştur'}
        </Button>
        <Button variant="secondary" onClick={cancelEdit} className="rounded-xl">
          İptal
        </Button>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6">
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast({ ...toast, isVisible: false })}
      />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Bannerlar</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Mobil ve web ana ekranındaki tanıtım bannerlarını yönetin
          </p>
        </div>
        {!isCreating && !editingId && (
          <Button variant="primary" onClick={openCreate} className="rounded-xl font-semibold">
            + Yeni Banner
          </Button>
        )}
      </div>

      {(isCreating || editingId) && formPanel}

      {isLoading ? (
        <div className="text-center py-16 text-gray-400">Yükleniyor...</div>
      ) : banners.length === 0 ? (
        <Card padding="lg" className="text-center py-16 text-gray-500">
          <p className="font-medium mb-1">Henüz banner eklenmemiş</p>
          <p className="text-sm">Yeni banner oluşturarak başlayın.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {banners.map((banner) => (
            <Card key={banner.id} padding="md" className="flex items-center gap-4">
              <div
                className="w-20 h-14 rounded-xl flex-shrink-0 flex items-center justify-center text-white font-bold text-xs overflow-hidden"
                style={{ backgroundColor: banner.backgroundColor || '#E63946' }}
              >
                {banner.imageUrl ? (
                  <img src={banner.imageUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-center leading-tight px-1">{banner.title.slice(0, 20)}</span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-gray-900 truncate">{banner.title}</p>
                  <Badge variant={banner.isActive ? 'success' : 'secondary'} className="rounded-lg text-xs">
                    {banner.isActive ? 'Aktif' : 'Pasif'}
                  </Badge>
                  <Badge variant="secondary" className="rounded-lg text-xs">
                    {PLATFORM_LABELS[banner.platform]}
                  </Badge>
                  <span className="text-xs text-gray-400">Sıra: {banner.displayOrder}</span>
                </div>
                {banner.subtitle && (
                  <p className="text-sm text-gray-500 mt-0.5 truncate">{banner.subtitle}</p>
                )}
                {(banner.startDate || banner.endDate) && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {banner.startDate ? banner.startDate.slice(0, 10) : '...'} →{' '}
                    {banner.endDate ? banner.endDate.slice(0, 10) : '...'}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => openEdit(banner)}
                  className="p-2 rounded-xl text-gray-500 hover:bg-primary/10 hover:text-primary transition-colors"
                  title="Düzenle"
                >
                  ✏️
                </button>
                <button
                  type="button"
                  onClick={() => deleteBanner(banner.id)}
                  disabled={deletingId === banner.id}
                  className="p-2 rounded-xl text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                  title="Sil"
                >
                  🗑️
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
